/**
 * 极练 Rehab & Fit —— 纯函数疲劳累积与中枢负荷引擎（阶段 7）。
 *
 * 铁律：
 * 1. 纯函数、零依赖、时间与历史作为参数传入；
 * 2. 基于 Banister 脉冲响应 / ATL-CTL-TSB 模型及确定性阈值；
 * 3. 输出携带可解释 reasonCodes 与 ruleVersion。
 */

export interface DailyWorkoutLoad {
  localDate: string; // YYYY-MM-DD
  volumeKg: number;
  completedSets: number;
  highIntensitySetsCount: number; // RIR 0 或 1 的高强度极限组数
  durationMinutes: number;
}

export interface DailyReadinessRecord {
  localDate: string;
  fatigueLevel: "low" | "medium" | "high";
  sleepQuality: "good" | "moderate" | "poor";
  muscleSoreness: "none" | "mild" | "significant";
}

export interface FatigueEngineInput {
  referenceDate: string; // 当前评估日期 YYYY-MM-DD
  historyLoads: DailyWorkoutLoad[];
  historyReadiness?: DailyReadinessRecord[];
  weeksSinceLastDeload?: number; // 连续未减量周数
}

export type DeloadUrgency = "none" | "suggested" | "mandatory";

export interface FatigueEngineResult {
  referenceDate: string;
  acuteTrainingLoad: number; // ATL (过去 7 天负荷加权)
  chronicTrainingLoad: number; // CTL (过去 28 天负荷加权)
  trainingStressBalance: number; // TSB = CTL - ATL
  fatigueIndex: number; // 0..100 深层中枢疲劳指数
  highIntensityRatio: number; // 近期极限组占比 0..1
  consecutiveHighFatigueDays: number;
  deloadUrgency: DeloadUrgency;
  reasonCodes: string[];
  recommendationZh: string;
  ruleVersion: string;
}

export const FATIGUE_RULE_VERSION = "1.0.0";

/** 计算两天之间的相差天数 */
function daysDiff(d1: string, d2: string): number {
  const t1 = new Date(`${d1}T00:00:00Z`).getTime();
  const t2 = new Date(`${d2}T00:00:00Z`).getTime();
  return Math.round((t1 - t2) / 86_400_000);
}

export function evaluateFatigueAccumulation(input: FatigueEngineInput): FatigueEngineResult {
  const ref = input.referenceDate;
  const loads = input.historyLoads ?? [];
  const readiness = input.historyReadiness ?? [];
  const weeksSinceDeload = input.weeksSinceLastDeload ?? 0;

  // 1. 计算 ATL (7 天半衰加权) 与 CTL (28 天半衰加权)
  let atlSum = 0;
  let atlWeightSum = 0;
  let ctlSum = 0;
  let ctlWeightSum = 0;
  let recentHighIntensitySets = 0;
  let recentTotalSets = 0;

  for (const item of loads) {
    const diff = daysDiff(ref, item.localDate);
    if (diff < 0 || diff > 42) continue; // 忽略未来日期及超过 6 周前的记录

    // 负荷指数 = 总容量/100 + 组数*10 + 极限组*15
    const dailyLoad = (item.volumeKg / 100) + (item.completedSets * 10) + (item.highIntensitySetsCount * 15);

    // 7天 ATL 衰减 (e^(-diff/7))
    if (diff <= 7) {
      const wAtl = Math.exp(-diff / 7);
      atlSum += dailyLoad * wAtl;
      atlWeightSum += wAtl;
      recentHighIntensitySets += item.highIntensitySetsCount;
      recentTotalSets += item.completedSets;
    }

    // 28天 CTL 衰减 (e^(-diff/28))
    if (diff <= 28) {
      const wCtl = Math.exp(-diff / 28);
      ctlSum += dailyLoad * wCtl;
      ctlWeightSum += wCtl;
    }
  }

  const acuteTrainingLoad = atlWeightSum > 0 ? Math.round((atlSum / atlWeightSum) * 10) / 10 : 0;
  const chronicTrainingLoad = ctlWeightSum > 0 ? Math.round((ctlSum / ctlWeightSum) * 10) / 10 : 0;
  const trainingStressBalance = Math.round((chronicTrainingLoad - acuteTrainingLoad) * 10) / 10;

  const highIntensityRatio =
    recentTotalSets > 0 ? Math.round((recentHighIntensitySets / recentTotalSets) * 100) / 100 : 0;

  // 2. 分析主观恢复疲劳天数 (连续疲劳天数)
  let consecutiveHighFatigueDays = 0;
  const sortedReadiness = [...readiness]
    .filter((r) => r.localDate <= ref)
    .sort((a, b) => b.localDate.localeCompare(a.localDate));

  for (const r of sortedReadiness) {
    if (r.fatigueLevel === "high" || r.sleepQuality === "poor") {
      consecutiveHighFatigueDays++;
    } else {
      break;
    }
  }

  // 3. 计算 0-100 中枢疲劳指数 (Fatigue Index)
  let fatigueScore = 20; // 基础值

  // TSB 负向惩罚（急性负荷超额）
  if (trainingStressBalance < -30) {
    fatigueScore += 35;
  } else if (trainingStressBalance < -15) {
    fatigueScore += 20;
  } else if (trainingStressBalance < 0) {
    fatigueScore += 10;
  }

  // 极限组过量惩罚
  if (highIntensityRatio > 0.45) {
    fatigueScore += 25;
  } else if (highIntensityRatio > 0.3) {
    fatigueScore += 15;
  }

  // 主观连续疲劳
  if (consecutiveHighFatigueDays >= 3) {
    fatigueScore += 25;
  } else if (consecutiveHighFatigueDays >= 2) {
    fatigueScore += 15;
  }

  // 连续训练周数
  if (weeksSinceDeload >= 6) {
    fatigueScore += 20;
  } else if (weeksSinceDeload >= 4) {
    fatigueScore += 10;
  }

  const fatigueIndex = Math.min(100, Math.max(0, fatigueScore));

  // 4. 判定减量等级 (Deload Urgency) 与原因码
  const reasonCodes: string[] = [];
  let deloadUrgency: DeloadUrgency = "none";
  let recommendationZh = "当前疲劳累积处于安全可控区间，可按既定双进阶计划正常推进。";

  if (fatigueIndex >= 75 || consecutiveHighFatigueDays >= 4 || (weeksSinceDeload >= 7 && trainingStressBalance < -20)) {
    deloadUrgency = "mandatory";
    if (consecutiveHighFatigueDays >= 4) reasonCodes.push("readiness_fatigue_streak");
    if (weeksSinceDeload >= 7) reasonCodes.push("deload_overdue_7w");
    if (trainingStressBalance < -30) reasonCodes.push("tsb_extreme_deficit");
    if (highIntensityRatio > 0.4) reasonCodes.push("rir0_excessive");
    recommendationZh = "⚠️ 中枢神经与肌纤维深层疲劳已达临界点，强烈建议开启为期 1 周的主动减量周（降量 40%，维持 RIR 2–3）。";
  } else if (fatigueIndex >= 50 || weeksSinceDeload >= 5 || trainingStressBalance < -15) {
    deloadUrgency = "suggested";
    if (weeksSinceDeload >= 5) reasonCodes.push("deload_recommended_5w");
    if (trainingStressBalance < -15) reasonCodes.push("acute_load_spike");
    if (consecutiveHighFatigueDays >= 2) reasonCodes.push("fatigue_elevated");
    recommendationZh = "近期训练负荷较高且有轻度疲劳累积，建议在接下来 1–2 周内安排一次低疲劳恢复周或轻量日。";
  }

  return {
    referenceDate: ref,
    acuteTrainingLoad,
    chronicTrainingLoad,
    trainingStressBalance,
    fatigueIndex,
    highIntensityRatio,
    consecutiveHighFatigueDays,
    deloadUrgency,
    reasonCodes,
    recommendationZh,
    ruleVersion: FATIGUE_RULE_VERSION,
  };
}
